import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@repo/common';
import { logoutUser } from '../../api/user/user.api';
import { Modal } from '../../components/TwoDays/Modal';
import { JOB_LABEL, cn } from '../../components/TwoDays/model/derive';
import { KST, formatDue } from '../../components/TwoDays/model/time';
import { useWorkboardCtx } from '../../components/TwoDays/model/useWorkboard';
import TdButton from '../../components/TwoDays/Button';
import SoundSettings from '../../components/TwoDays/SoundSettings';

/**
 * 워크보드 Settings — 프로젝트·계정은 누구에게나 읽기 전용. 알림음 설정만 이 브라우저에 저장한다.
 * Consent 는 Pre-join 동의서 7개 항목(약관 v1.3 제7조)을 읽기 전용으로 보여준다.
 * 시작 전·진행 중·종료 모두 같고, 종료되면 맨 위에 안내 한 줄만 더한다.
 */

const card = 'flex flex-col rounded-2xl bg-grayscale-gray00 p-6 shadow-4';
const at = (iso) => (iso ? `${formatDue(iso, KST)} KST` : '–');

const accountOf = (user, viewer, me) => ({
  name:
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.nickname ||
    viewer?.name ||
    '–',
  email: user?.email || me?.email || '–',
});

/** 라벨 · 값 한 줄. 모바일은 라벨 위에 값 */
const Row = ({ label, children }) => (
  <div className='grid gap-1 py-2.5 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4'>
    <dt className='text-[13px] text-text-secondary'>{label}</dt>
    <dd className='min-w-0 text-[14px] text-text-primary'>{children}</dd>
  </div>
);

/** [라벨, 동의 여부] — 동의서 순서. 필수 4개 · 선택 3개. 예전 합류자는 없는 항목이 있다 */
const CONSENT_ROWS = [
  [
    'Terms of Use and Privacy Policy',
    (me) => (me?.consentedAt ? true : null),
    (me) => me?.consentTermsVersion,
  ],
  ['Work records', (me) => me?.consentDataUse],
  ['Peer review', (me) => me?.consentPeerReview],
  ['Transfer outside Korea', (me) => me?.consentOverseasTransfer],
  ['Promotional use', (me) => me?.consentPromoUse],
  ['Real name display', (me) => (me?.nameDisplay ? me.nameDisplay === 'real' : null)],
  ['Self-check results', (me) => me?.consentSelfCheckLink],
];

const ConsentModal = ({ me, open, onClose }) => (
  <Modal
    open={open}
    title='Your consent'
    subtitle='What you agreed to in Pre-join. Read-only. To withdraw an optional item, email contact@ko-ready.com.'
    onClose={onClose}
    sheetOnMobile
    footer={
      <TdButton variant='soft' onClick={onClose}>
        Close
      </TdButton>
    }
  >
    <dl className='-my-1 flex flex-col'>
      {CONSENT_ROWS.map(([label, valueOf, noteOf]) => {
        const agreed = valueOf(me) === true;
        const note = noteOf?.(me);
        return (
          <div
            key={label}
            className='flex items-center justify-between gap-4 border-t border-green-deep03/[0.08] py-3 text-[14px]'
          >
            <dt className='text-text-primary'>
              {label}
              {note && <span className='ml-1.5 text-text-secondary'>{note}</span>}
            </dt>
            <dd
              className={cn(
                'font-medium',
                agreed ? 'text-functional-success03' : 'text-text-secondary',
              )}
            >
              {agreed ? 'Agreed' : 'Not agreed'}
            </dd>
          </div>
        );
      })}
    </dl>
  </Modal>
);

const TwoDaysSettings = () => {
  const { data, viewer, readOnly } = useWorkboardCtx();
  const { user } = useAuth() ?? {};
  const [consentOpen, setConsentOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const { project, members } = data;
  const me = data.me ?? null;
  const leader = members.find((m) => m.role === 'leader');
  const account = accountOf(user, viewer, me);
  const completed = project.status === 'ended';

  const logout = async () => {
    setLoggingOut(true);
    try {
      await logoutUser();
    } finally {
      window.location.assign('/');
    }
  };

  return (
    <div className='flex max-w-[880px] flex-col gap-6'>
      <h1 className='text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>
        Settings
      </h1>

      {completed && (
        <p className='rounded-xl border border-green-deep03/[0.12] bg-surface-green10 px-4 py-3 text-[13px] text-text-primary'>
          This project is completed, so project details are read-only.
        </p>
      )}

      <section className={card}>
        <h2 className='mb-2 text-[16px] font-medium text-text-primary'>Project</h2>
        <dl className='flex flex-col'>
          <Row label='Project name'>{project.title}</Row>
          <Row label='Scenario'>{project.scenario || project.briefTitle || '–'}</Row>
          <Row label='Team'>{project.teamName}</Row>
          {viewer && viewer.role !== 'npc' && (
            <Row label='Your job'>{JOB_LABEL[viewer.job] ?? viewer.job}</Row>
          )}
          <Row label='Leader'>
            {leader ? `${leader.name} · ${JOB_LABEL[leader.job]}` : 'No leader'}
          </Row>
          <Row label='Start'>{at(project.startAt)}</Row>
          <Row label='End'>{at(project.endAt)}</Row>
        </dl>
      </section>

      <section className={card}>
        <h2 className='mb-2 text-[16px] font-medium text-text-primary'>Account</h2>
        <dl className='flex flex-col'>
          <Row label='Name'>{account.name}</Row>
          <Row label='Email'>
            <span className='break-all'>{account.email}</span>
          </Row>
        </dl>
        <div className='mt-3 flex flex-wrap items-center justify-between gap-3'>
          {/* 마이페이지 › Account setting 탭으로 — 앱 안에서 옮겨야 로그인 상태가 이어진다 (새로 불러오면 홈으로 튕긴다) */}
          <Link
            to='/mypage?tab=Account%20setting'
            className='text-[13px] font-medium text-green-deep03 underline underline-offset-2'
          >
            Manage your Koready account
          </Link>
          <TdButton variant='soft' onClick={logout} disabled={loggingOut} className='px-4'>
            Log out
          </TdButton>
        </div>
      </section>

      <SoundSettings description='Plays when a new notification arrives and when a checkpoint opens, while the board is open.' />

      {/* 스태프 열람은 Pre-join 을 하지 않아 동의 내역이 없다 */}
      {!readOnly && me && (me.consentedAt || me.consentPeerReview != null) && (
        <section className={card}>
          <h2 className='mb-2 text-[16px] font-medium text-text-primary'>Consent</h2>
          <button
            type='button'
            onClick={() => setConsentOpen(true)}
            className='w-fit text-[13px] font-medium text-green-deep03 underline underline-offset-2'
          >
            View your consent
          </button>
        </section>
      )}

      <ConsentModal me={me} open={consentOpen} onClose={() => setConsentOpen(false)} />
    </div>
  );
};

export default TwoDaysSettings;
